import type { AttendanceDay } from '@wf/shared'
import { formatHMS } from '../time/format'
import { shiftDay } from './timeMath'

const WD = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'] as const
/** Skalen-Deckel des Balkendiagramms: 9h füllen die volle Höhe (wie Design ~540 min). */
const MAX_SECONDS = 9 * 3600

/**
 * Karte „Diese Woche" (Design 1a): SVG-Balken Mo–Sa aus den Server-Tageszeilen der laufenden Woche,
 * heutiger Balken hervorgehoben + live wachsend (liveExtraSeconds). CSP-fest: Geometrie über
 * SVG-Attribute, Farben über Klassen/Tokens.
 */
export function WeekChart({ weekStart, days, today, liveExtraSeconds }: {
  weekStart: string
  days: readonly AttendanceDay[]
  today: string
  liveExtraSeconds: number
}) {
  const byDay = new Map(days.map((d) => [d.day, d.workedSeconds]))
  const sunday = shiftDay(weekStart, 6)
  const sundaySeconds = (byDay.get(sunday) ?? 0) + (sunday === today ? liveExtraSeconds : 0)
  // So-Balken nur, wenn wirklich Sonntagsarbeit anfiel — sonst bleibt die Mo–Sa-Werkstattwoche ruhig.
  const labels: string[] = sundaySeconds > 0 ? [...WD, 'So'] : [...WD]
  const bars = labels.map((label, i) => {
    const day = shiftDay(weekStart, i)
    const isToday = day === today
    const seconds = (byDay.get(day) ?? 0) + (isToday ? liveExtraSeconds : 0)
    return { label, day, isToday, seconds }
  })
  // „Woche gesamt" über ALLE Report-Tage (inkl. So auch ohne eigenen Balken) — sonst fällt
  // Sonntagsarbeit still aus der Summe (Review-Fund).
  const total = [...byDay.values()].reduce((sum, s) => sum + s, 0) + liveExtraSeconds

  return (
    <div className="wf-card wf-zscard">
      <div className="wf-zscard__label">Diese Woche</div>
      <div className="wf-zsweek" aria-label="Arbeitszeit pro Wochentag">
        {bars.map((b) => (
          <div key={b.day} className="wf-zsweek__col">
            <svg width="100%" height="56" className="wf-zsweek__svg" aria-hidden="true">
              {/* Ruhige Spur hinter jedem Balken — sonst wirkt eine leere Woche kaputt statt leer. */}
              <rect className="wf-zsweek__track" x="30%" width="40%" y="0" height="100%" rx="3" />
              <rect
                className={b.isToday ? 'wf-zsweek__bar wf-zsweek__bar--today' : 'wf-zsweek__bar'}
                x="30%"
                width="40%"
                y={`${100 - Math.max(6, Math.min(100, (b.seconds / MAX_SECONDS) * 100))}%`}
                height={`${Math.max(6, Math.min(100, (b.seconds / MAX_SECONDS) * 100))}%`}
                rx="3"
              />
            </svg>
            <span className={b.isToday ? 'wf-zsweek__wd wf-zsweek__wd--today' : 'wf-zsweek__wd'}>{b.label}</span>
          </div>
        ))}
      </div>
      <div className="wf-zscard__foot">
        <span>Woche gesamt</span>
        <span className="wf-mono">{formatHMS(total)}</span>
      </div>
    </div>
  )
}
